import React from "react";
import { motion } from "framer-motion";
import { Button } from "../../../../components/ui/button";
import { Link } from "next/link";

const OverlapGroupSection = () => {
  // Container animation for staggered effects
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  // Text animation with scroll-triggered fade-in
  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.8,
      },
    },
  };

  // Button animation with scroll-triggered scale and hover effects
  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 20,
        duration: 0.8,
      },
    },
    hover: {
      scale: 1.1,
      boxShadow: "0 8px 16px rgba(255, 174, 0, 0.3)",
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.95 },
  };

  return (
    <section className="w-full bg-white">
      <motion.div
        className="w-full h-auto bg-[url(/bg-image.svg)] bg-cover bg-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="max-w-[1440px] mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-[137px] py-[40px] md:py-[53px] md:px-[147px]">
            <motion.div
              variants={textVariants}
              className="text-center md:text-left max-w-[515px] font-bricolage"
            >
              <motion.h2
                className="text-white font-bold text-2xl sm:text-3xl lg:text-4xl leading-snug mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Don’t just scroll past stories — start living one.
              </motion.h2>

              <motion.p
                className="text-white font-medium text-base sm:text-lg lg:text-xl leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Join a community of{" "}
                <motion.span
                  className="font-semibold text-[#FFAE00]"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  explorers, creators, and changemakers
                </motion.span>{" "}
                building clarity through curated travel.
              </motion.p>
            </motion.div>

            {/* Animated Join Community Button */}
            <motion.div
              className="text-center  w-full"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <a
                href="https://chat.whatsapp.com/your-whatsapp-group-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.button
                  className="relative inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-3.5 rounded-full border-2 border-[#FFAE00] bg-white text-[#FFAE00] font-bold transition-all duration-300 hover:text-white hover:shadow-lg overflow-hidden group text-sm sm:text-base"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 20px 40px rgba(255, 174, 0, 0.3)",
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <a
                    href="https://chat.whatsapp.com/C3SkpzYbUoj037GX3cPpsx"
                    target="_blank"
                  >
                    <span className="relative z-10 flex items-center">
                      <span className="group-hover:hidden">Join the Tribe</span>
                      <span className="hidden group-hover:inline">
                        You’re In!
                      </span>

                      <motion.span
                        className="ml-3"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        →
                      </motion.span>
                    </span>
                  </a>

                  <span className="absolute inset-0 z-0 bg-gradient-to-r from-[#FFAE00] to-[#FFB91A] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </motion.button>
              </a>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default OverlapGroupSection;
